// @flow

import * as React from "react";
import { useSelector } from "react-redux";
import { withRouter } from "react-router-dom";

import Config from "../constants/Config";
import HeroCheckout from "../components/organisms/HeroCheckout";
import Button from "../components/atoms/Button";
import { getEventRegistrations } from "../api/cart";
import TicketDetailModal from "../components/organisms/TicketDetailModal";
import promiseAllSequential from "../utils/promiseAllSequential";

type PropsType = {
    match: any,
};

type GuestEventRegistrationDataType = {
    status: "completed" | "in-progress",
    // eslint-disable-next-line flowtype/generic-spacing
    events: Record<
        string,
        {
            eventId: string,
            startDate: string,
            endDate: string,
            fullName: string,
            building: string,
            tickets: Record<string, any>[],
        }
    >,
};

const startGuestEventRegistrationPoller = (
    orderId: string,
    ticketToken: string,
    onUpdate: (data: any) => void,
    imageCheckerRef: any
): (() => void) => {
    const pollInterval = 5000;

    const testImage = imageUrl =>
        new Promise(resolve => {
            const imageCheckerElement = imageCheckerRef?.current;
            imageCheckerElement.src = imageUrl;
            imageCheckerElement.onerror = () => resolve(false);
            imageCheckerElement.onload = () => resolve(true);
        });

    let timeoutId;
    const processedGuestEventRegistrations: GuestEventRegistrationDataType = {
        status: "in-progress",
        events: {},
    };

    const pollGuestEventRegistrations = async () => {
        let updateRequired = false;

        const response = await getEventRegistrations(orderId, ticketToken);

        const updatedGuestEventRegistration = {
            ...processedGuestEventRegistrations,
        };
        await promiseAllSequential(
            response.tickets.map(ticket => async () => {
                const {
                    event_id,
                    start_date,
                    end_date,
                    first_name,
                    last_name,
                    building,
                    event_name,
                    type,
                    email,
                    qrcode_url,
                } = ticket;
                let event = updatedGuestEventRegistration.events[event_id];
                if (!event) {
                    event = {
                        eventId: event_id,
                        startDate: start_date,
                        endDate: end_date,
                        eventName: event_name,
                        location: building,
                        type,
                        tickets: [],
                    };
                }

                if (
                    qrcode_url &&
                    !event.tickets.find(
                        processedTicket =>
                            qrcode_url === processedTicket.qrcodeUrl
                    )
                ) {
                    const imageExists =
                        imageCheckerRef && (await testImage(qrcode_url));
                    if (imageExists) {
                        updatedGuestEventRegistration.events[event_id] = event;
                        event.tickets.push({
                            firstName: first_name,
                            lastName: last_name,
                            email,
                            qrcodeUrl: qrcode_url,
                        });
                        updateRequired = true;
                    }
                }
            })
        );

        if (
            response.status === "in-progress" ||
            Object.values(updatedGuestEventRegistration.events).flatMap(
                event => event.tickets
            ).length !== response.tickets.length
        ) {
            timeoutId = setTimeout(pollGuestEventRegistrations, pollInterval);
        } else {
            updatedGuestEventRegistration.status = "completed";
            updateRequired = true;
        }

        if (updateRequired && timeoutId) {
            onUpdate({
                ...updatedGuestEventRegistration,
                events: Object.values(updatedGuestEventRegistration.events),
            });
        }
    };
    timeoutId = setTimeout(pollGuestEventRegistrations(), 0);

    return () => {
        clearTimeout(timeoutId);
        timeoutId = null;
    };
};

const CheckoutSuccess = (props: PropsType): React.Node => {
    const { location } = useSelector((state: any): any => state.router);
    const { loggedIn } = useSelector((state: any): any => state.user);
    const [orderHasTickets] = React.useState(location.query.t);
    const [guestEventRegistration, setGuestEventRegistration] =
        React.useState<GuestEventRegistrationData>({
            status: "in-progress",
            events: [],
        });
    const [guestTicketModalOpened, setGuestTicketModalOpened] =
        React.useState(false);
    const ticketToken = location.query.ticket_token;
    const orderId = props.match.params.orderId;

    const imageCheckerRef = React.useRef();

    React.useEffect(() => {
        if (
            orderId &&
            ticketToken &&
            guestTicketModalOpened &&
            guestEventRegistration.status !== "completed"
        ) {
            return startGuestEventRegistrationPoller(
                orderId,
                ticketToken,
                setGuestEventRegistration,
                imageCheckerRef
            );
        }
    }, [
        orderId,
        ticketToken,
        guestTicketModalOpened,
        setGuestEventRegistration,
        guestEventRegistration.status,
    ]);

    React.useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
        });
        window.dispatchEvent(new Event("resize"));
    }, []);

    return (
        <div className="checkout-page">
            <HeroCheckout heading="Success!" disableCheckoutNav={true} />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-xs-12 col-sm-10 col-md-8">
                        <div className="body-18">
                            Thanks for your order {orderId}.<br />
                            Check your email for details of your purchase and
                            know you've done a good thing - every dollar from
                            your purchase contributes to the delivery of UTS
                            student programs, including clubs and societies,
                            events and sporting programs.
                        </div>
                        <br />
                        <br />
                        <div className="row start-xs">
                            {loggedIn ? (
                                <>
                                    {orderHasTickets ? (
                                        <>
                                            <div className="col-xs-12 col-sm-inline">
                                                <Button
                                                    inline={true}
                                                    styles={["green"]}
                                                    to={
                                                        Config.routes
                                                            .incomingTickets
                                                    }
                                                >
                                                    View Tickets
                                                </Button>
                                            </div>

                                            <div className="col-xs-12 col-sm-inline">
                                                <Button
                                                    inline={true}
                                                    to={`${Config.routes.purchases}/${orderId}`}
                                                >
                                                    View Order Details
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="col-xs-12 col-sm-inline">
                                            <Button
                                                styles={["green"]}
                                                to={`${Config.routes.purchases}/${orderId}`}
                                            >
                                                View Order Details
                                            </Button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="col-xs-12 col-sm-inline">
                                        <Button
                                            inline={ticketToken}
                                            styles={["green"]}
                                            to="/"
                                        >
                                            Go to Homepage
                                        </Button>
                                    </div>
                                </>
                            )}
                            {ticketToken ? (
                                <>
                                    <div className="col-xs-12 col-sm-inline">
                                        <Button
                                            inline={ticketToken}
                                            styles={["green-secondary"]}
                                            onClick={() =>
                                                setGuestTicketModalOpened(true)
                                            }
                                        >
                                            View Tickets
                                        </Button>
                                    </div>
                                    <TicketDetailModal
                                        data={guestEventRegistration}
                                        onClose={() =>
                                            setGuestTicketModalOpened(false)
                                        }
                                        active={guestTicketModalOpened}
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
            <img alt="" style={{ display: "none" }} ref={imageCheckerRef} />
        </div>
    );
};

export default withRouter(CheckoutSuccess);
